'use client'

import { useCallback, useRef, useState } from 'react'

type SpeakParams = {
  text: string
  voice?: string
  provider?: 'gemini' | 'openai' | 'elevenlabs' | 'fal'
  mode?: 'realtime_conversation' | 'cinematic_line' | 'narration' | 'fallback' | 'experimental'
  format?: 'mp3' | 'wav'
  instructions?: string
}

type SpeechErrorCode = 'NO_CREDITS' | 'PLAYBACK_ERROR' | 'UNKNOWN'

export function useSynSpeech() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const objectUrlRef = useRef<string | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const [lastErrorCode, setLastErrorCode] = useState<SpeechErrorCode | null>(null)
  const [lastMeta, setLastMeta] = useState<{
    provider?: string
    creditsSpent?: number
    requiredCredits?: number
    balance?: number
  } | null>(null)

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setIsPlaying(false)
  }, [])

  const cleanupUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
  }, [])

  const speak = useCallback(async (params: SpeakParams) => {
    const text = String(params.text || '').trim()
    if (!text) return { ok: false as const, errorCode: 'UNKNOWN' as const }

    setLastError(null)
    setLastErrorCode(null)
    setIsLoading(true)

    try {
      stop()
      cleanupUrl()

      const res = await fetch('/api/syn/speak', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          text,
          voice: params.voice || 'syn_default',
          provider: params.provider,
          mode: params.mode || 'realtime_conversation',
          format: params.format || 'mp3',
          instructions: params.instructions,
        }),
      })

      if (!res.ok) {
        let msg = `TTS failed (${res.status})`
        let code: SpeechErrorCode = 'UNKNOWN'
        let requiredCredits: number | undefined
        let balance: number | undefined

        try {
          const json = await res.json()

          if (json?.error === 'NO_CREDITS' || res.status === 402) {
            code = 'NO_CREDITS'
            msg = 'NO_CREDITS'
            requiredCredits = Number(json?.required || 0) || undefined
            balance = Number(json?.balance || 0) || undefined
          } else {
            msg = json?.error || msg
          }
        } catch {}

        setLastError(msg)
        setLastErrorCode(code)
        setLastMeta((prev) => ({
          ...(prev || {}),
          requiredCredits,
          balance,
        }))

        return {
          ok: false as const,
          errorCode: code,
          message: msg,
          requiredCredits,
          balance,
        }
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      objectUrlRef.current = url

      const provider = res.headers.get('X-TTS-Provider') || undefined
      const creditsSpent = Number(res.headers.get('X-Credits-Spent') || 0) || undefined

      setLastMeta({
        provider,
        creditsSpent,
      })

      const audio = new Audio(url)
      audioRef.current = audio

      audio.onplay = () => setIsPlaying(true)
      audio.onended = () => setIsPlaying(false)
      audio.onpause = () => setIsPlaying(false)
      audio.onerror = () => {
        setIsPlaying(false)
        setLastErrorCode('PLAYBACK_ERROR')
        setLastError('No se pudo reproducir el audio')
      }

      await audio.play()

      return {
        ok: true as const,
        provider,
        creditsSpent,
      }
    } catch (err: any) {
      const msg = err?.message || 'Error speaking with SYN'
      setLastError(msg)
      setLastErrorCode('UNKNOWN')
      setIsPlaying(false)

      return {
        ok: false as const,
        errorCode: 'UNKNOWN' as const,
        message: msg,
      }
    } finally {
      setIsLoading(false)
    }
  }, [cleanupUrl, stop])

  return {
    speak,
    stop,
    isLoading,
    isPlaying,
    lastError,
    lastErrorCode,
    lastMeta,
  }
}